import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import Text from 'common/ui/Text';
import { Stack, Spacer } from 'common/ui/Layout';
import { Colors } from 'styles';
import {
  ANNOTATION_COLOR_OPTIONS,
  annotationColorSettingsState,
} from 'domains/viewer/ViewportDre/Annotations/helpers/annotationColorSettings';
import type { AnnotationColors } from 'domains/viewer/ViewportDre/Annotations/helpers/useAnnotationColors';

type ColorRowProps = {
  label: string;
  description: string;
  selected: string;
  onSelect: (color: string) => void;
};

const Swatch = styled.button<{ $color: string; $selected: boolean }>`
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  cursor: pointer;
  border-radius: 2px;
  background-color: ${({ $color }) => $color};
  border: 2px solid ${({ $selected }) => ($selected ? Colors.blue3 : 'transparent')};
  outline: ${({ $selected }) => ($selected ? `1px solid ${Colors.blue3}` : 'none')};
  outline-offset: 1px;
`;

const ColorRow = ({ label, description, selected, onSelect }: ColorRowProps) => (
  <Stack alignY="center">
    <Stack vertical space="xsmall">
      <Text>{label}</Text>
      <Text variant="small">{description}</Text>
    </Stack>
    <Spacer />
    <Stack role="radiogroup" aria-label={label} space="small">
      {ANNOTATION_COLOR_OPTIONS.map(({ name, value }) => (
        <Swatch
          key={value}
          type="button"
          role="radio"
          aria-label={`${label}: ${name}`}
          aria-checked={value === selected}
          $color={value}
          $selected={value === selected}
          onClick={() => onSelect(value)}
        />
      ))}
    </Stack>
  </Stack>
);

export const AnnotationColorSettings = (): React.ReactElement => {
  const [colors, setColors] = useRecoilState(annotationColorSettingsState);

  const choose = (key: keyof AnnotationColors) => (color: string) =>
    setColors((current) => ({ ...current, [key]: color }));

  return (
    <>
      <Text>Annotation color</Text>
      <ColorRow
        label="Default color"
        description="The color your annotation normally appears in"
        selected={colors.defaultColor}
        onSelect={choose('defaultColor')}
      />
      <ColorRow
        label="Hover color"
        description="The color it changes to when you hover over or click it"
        selected={colors.hoverColor}
        onSelect={choose('hoverColor')}
      />
    </>
  );
};

AnnotationColorSettings.displayName = 'AnnotationColorSettings';
